import { useState, useEffect, useMemo } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import { formatNumber } from "../../../utils/formatNumber";
import { useAuth } from "../../../contexts/AuthContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const TAX_RATE = 0.07;

const STATUS_OPTIONS = [
  { label: "ส่งแล้ว", value: "sent" },
  { label: "ยืนยันแล้ว", value: "confirm" },
  { label: "รับเงินแล้ว", value: "payed" },
];

export default function SalesByProductSummary() {
  const auth = useAuth();

  const formatDate = (d) => d.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]); // grouped by product
  const [openSet, setOpenSet] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0].value);

  // sort
  const [orderBy, setOrderBy] = useState("productTitle");
  const [order, setOrder] = useState("asc");

  // inventory avg cost
  const [invById, setInvById] = useState({});
  const [invByName, setInvByName] = useState({});

  const canSeeCost = useMemo(() => {
    if (!auth) return false;
    const perm = auth?.permissions ?? {};
    return ["report_show_cost"].some((k) => perm?.[k] === true);
  }, [auth]);

  const canSeeProfit = useMemo(() => {
    if (!auth) return false;
    const perm = auth?.permissions ?? {};
    return ["report_show_profit"].some((k) => perm?.[k] === true);
  }, [auth]);

  // ====== Inventory: avg cost ======
  useEffect(() => {
    const loadInventory = async () => {
      try {
        const invRes = await services.getInventory();
        const invRaw = invRes?.data?.data ?? invRes?.data ?? invRes ?? [];
        const _byId = {};
        const _byName = {};
        (Array.isArray(invRaw) ? invRaw : [invRaw]).forEach((rec) => {
          const avg =
            Number(
              rec?.avgCostPerUnit ??
                rec?.averageCost ??
                rec?.avgCost ??
                rec?.costPerUnit ??
                rec?.cost ??
                0
            ) || 0;
          const pid =
            rec?.productId ??
            rec?.product?.id ??
            rec?.id ??
            rec?.product_id ??
            null;
          const pname =
            rec?.productName ??
            rec?.product?.title ??
            rec?.name ??
            rec?.product_name ??
            null;

          if (pid != null)
            _byId[String(pid)] = {
              avgCostPerUnit: avg,
              name: pname ?? undefined,
            };
          if (pname)
            _byName[String(pname).trim()] = {
              avgCostPerUnit: avg,
              id: pid ?? undefined,
            };
        });
        setInvById(_byId);
        setInvByName(_byName);
      } catch (e) {
        console.error("loadInventory error", e);
        setInvById({});
        setInvByName({});
      }
    };
    loadInventory();
  }, []);

  // ====== Fetch sales ======
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getSaleTransactions({
          ...(selectedStatus ? { status: selectedStatus } : {}),
          startDate,
          endDate,
        });
        const raw = res?.data?.data ?? res?.data ?? res ?? [];
        const grouped = groupByProduct(raw, invById, invByName);
        setRows(grouped);
        setOpenSet(new Set());
      } catch (e) {
        console.error("fetch error", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedStatus, invById, invByName]);

  // ====== Filter ======
  const filteredRows = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    if (!q) return rows;
    return rows.filter((r) => {
      const code = String(r.productCode ?? "").toLowerCase();
      const title = String(r.productTitle ?? "").toLowerCase();
      const cat = String(r.categoryMain ?? "").toLowerCase();
      const sub = String(r.categorySub ?? "").toLowerCase();
      return (
        code.includes(q) ||
        title.includes(q) ||
        cat.includes(q) ||
        sub.includes(q)
      );
    });
  }, [rows, search]);

  // ====== Sorting (main) ======
  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    data.sort((a, b) => {
      let aVal, bVal;
      switch (orderBy) {
        case "productCode":
          aVal = a.productCode ?? "";
          bVal = b.productCode ?? "";
          break;
        case "productTitle":
          aVal = a.productTitle ?? "";
          bVal = b.productTitle ?? "";
          break;
        case "categoryMain":
          aVal = a.categoryMain ?? "";
          bVal = b.categoryMain ?? "";
          break;
        case "categorySub":
          aVal = a.categorySub ?? "";
          bVal = b.categorySub ?? "";
          break;

        case "sendWeight":
        case "deductWeight":
        case "destWeight":
        case "avgPrice":
        case "avgCost":
        case "amountBeforeVat":
        case "costAmount":
        case "profitBeforeTax":
          aVal = Number(a[orderBy] || 0);
          bVal = Number(b[orderBy] || 0);
          break;

        default:
          aVal = 0;
          bVal = 0;
      }
      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, "th");
        return order === "asc" ? cmp : -cmp;
      }
      const cmp = (aVal || 0) - (bVal || 0);
      return order === "asc" ? cmp : -cmp;
    });
    return data;
  }, [filteredRows, orderBy, order]);

  const requestSort = (key) => {
    if (["unit"].includes(key)) return; // หน่วยไม่ให้ sort
    if (orderBy === key) setOrder((p) => (p === "asc" ? "desc" : "asc"));
    else {
      setOrderBy(key);
      setOrder(
        ["productCode", "productTitle", "categoryMain", "categorySub"].includes(
          key
        )
          ? "asc"
          : "desc"
      );
    }
  };

  const getRowKey = (row, idx) =>
    String(row.productId ?? row.productCode ?? row.productTitle ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ====== Summary footer ======
  const summary = useMemo(() => {
    let send = 0,
      deduct = 0,
      dest = 0,
      before = 0,
      cost = 0,
      profit = 0;
    sortedRows.forEach((r) => {
      send += r.sendWeight || 0;
      deduct += r.deductWeight || 0;
      dest += r.destWeight || 0;
      before += r.amountBeforeVat || 0;
      cost += r.costAmount || 0;
      profit += r.profitBeforeTax || 0;
    });
    return { send, deduct, dest, before, cost, profit };
  }, [sortedRows]);

  // ====== Export helpers ======
  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const lines = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportSummary = () => {
    const dataset = sortedRows.map((r) => ({
      รหัส: r.productCode ?? "",
      สินค้า: r.productTitle ?? "",
      หมวดหมู่หลัก: r.categoryMain ?? "",
      หมวดหมู่ย่อย: r.categorySub ?? "",
      น้ำหนักส่งสุทธิ: fix2(r.sendWeight),
      น้ำหนักหัก: fix2(r.deductWeight),
      น้ำหนักปลายทางสุทธิ: fix2(r.destWeight),
      หน่วย: r.unit || "กก.",
      ราคาขาย: fix2(r.avgPrice),
      ราคาต้นทุน: fix2(r.avgCost),
      ยอดขายก่อนภาษี: fix2(r.amountBeforeVat),
      ต้นทุน: fix2(r.costAmount),
      กำไรก่อนภาษี: fix2(r.profitBeforeTax),
    }));
    exportToCSV(dataset, `sales_by_product_${startDate}_to_${endDate}.csv`);
  };

  const handleExportAllItems = () => {
    const rowsFlat = [];
    sortedRows.forEach((r) => {
      (r.children || []).forEach((c) => {
        rowsFlat.push({
          รหัส: r.productCode ?? "",
          สินค้า: r.productTitle ?? "",
          ลูกค้า: c.customerName ?? "-",
          น้ำหนักส่งสุทธิ: fix2(c.sendWeight),
          น้ำหนักหัก: fix2(c.deductWeight),
          น้ำหนักปลายทางสุทธิ: fix2(c.destWeight),
          หน่วย: c.unit || "กก.",
          ราคาขาย: fix2(c.pricePerUnit),
          ราคาต้นทุน: fix2(c.costPerUnit),
          ยอดก่อนภาษี: fix2(c.amountBeforeVat),
          ต้นทุน: fix2(c.costAmount),
          กำไรก่อนภาษี: fix2(c.profitBeforeTax),
        });
      });
    });
    exportToCSV(
      rowsFlat,
      `sales_by_product_items_${startDate}_to_${endDate}.csv`
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-2 xl:mb-0">
               <span className="w-1 h-8 bg-green-600 rounded-full"></span>
               สรุปยอดขายสินค้ารายชนิด
            </h1>

            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 w-full xl:w-auto">
                {/* Search */}
               <div className="relative w-full lg:w-64">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="รหัส / ชื่อสินค้า / หมวดหมู่"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                />
               </div>

                {/* Status Toggle */}
               <div className="flex bg-gray-100 p-1 rounded-xl">
                 {STATUS_OPTIONS.map((opt) => (
                   <button
                     key={opt.value}
                     onClick={() => setSelectedStatus(opt.value)}
                     className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                       selectedStatus === opt.value
                         ? "bg-white text-green-700 shadow-sm"
                         : "text-gray-500 hover:text-gray-700"
                     }`}
                   >
                     {opt.label}
                   </button>
                 ))}
               </div>

              {/* Date Pickers */}
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 w-full lg:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เริ่ม</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm w-full"
                />
              </div>

               <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 w-full lg:w-auto">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">สิ้นสุด</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm w-full"
                />
              </div>
            </div>
          </div>
          
           <div className="mt-6 flex gap-2 justify-end">
                 <button
                    onClick={handleExportSummary}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลหลัก</span>
                </button>
                 <button
                    onClick={handleExportAllItems}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลย่อยทั้งหมด</span>
                </button>
          </div>
        </div>

        {/* Content */}
         {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
             </div>
        ) : (
             <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                 <div className="overflow-x-auto">
                     <table className="w-full text-sm text-left">
                         <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                             <tr>
                                <th className="px-4 py-4 w-12"></th>
                                <th onClick={() => requestSort("productCode")} className="px-4 py-4 cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center gap-1">
                                        รหัส {orderBy === "productCode" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("productTitle")} className="px-4 py-4 cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center gap-1">
                                        สินค้า {orderBy === "productTitle" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("categoryMain")} className="px-4 py-4 cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center gap-1">
                                        หมวดหมู่หลัก {orderBy === "categoryMain" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("categorySub")} className="px-4 py-4 cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center gap-1">
                                        หมวดหมู่ย่อย {orderBy === "categorySub" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("sendWeight")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center justify-end gap-1">
                                        ส่งสุทธิ {orderBy === "sendWeight" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("deductWeight")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center justify-end gap-1">
                                        หัก {orderBy === "deductWeight" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("destWeight")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center justify-end gap-1">
                                        ปลายทางสุทธิ {orderBy === "destWeight" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4">หน่วย</th>
                                <th onClick={() => requestSort("avgPrice")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center justify-end gap-1">
                                        ราคาขาย {orderBy === "avgPrice" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                {canSeeCost && (
                                     <th onClick={() => requestSort("avgCost")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                        <div className="flex items-center justify-end gap-1">
                                            ราคาต้นทุน {orderBy === "avgCost" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                        </div>
                                    </th>
                                )}
                                <th onClick={() => requestSort("amountBeforeVat")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                     <div className="flex items-center justify-end gap-1">
                                        ยอดก่อนภาษี {orderBy === "amountBeforeVat" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                {canSeeCost && (
                                     <th onClick={() => requestSort("costAmount")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                        <div className="flex items-center justify-end gap-1">
                                            ต้นทุน {orderBy === "costAmount" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                        </div>
                                    </th>
                                )}
                                {canSeeProfit && (
                                     <th onClick={() => requestSort("profitBeforeTax")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100">
                                        <div className="flex items-center justify-end gap-1">
                                            กำไรก่อนภาษี {orderBy === "profitBeforeTax" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                        </div>
                                    </th>
                                )}
                             </tr>
                         </thead>
                         <tbody className="divide-y divide-gray-50">
                             {sortedRows.map((row, idx) => {
                                 const key = getRowKey(row, idx);
                                 const isOpen = openSet.has(key);
                                 return (
                                     <RowGroup
                                        key={key}
                                        isOpen={isOpen}
                                        onToggle={() => toggleOpen(key)}
                                        row={row}
                                        canSeeCost={canSeeCost}
                                        canSeeProfit={canSeeProfit}
                                        formatNumber={formatNumber}
                                     />
                                 );
                             })}
                         </tbody>
                         <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                             <tr>
                                <td colSpan={5} className="px-4 py-4 text-right">
                                    รวม {sortedRows.length.toLocaleString("th-TH")} ชนิด
                                </td>
                                <td className="px-4 py-4 text-right">{formatNumber(summary.send)}</td>
                                <td className="px-4 py-4 text-right">{formatNumber(summary.deduct)}</td>
                                <td className="px-4 py-4 text-right">{formatNumber(summary.dest)}</td>
                                <td className="px-4 py-4">กก.</td>
                                <td className="px-4 py-4"></td>
                                {canSeeCost && <td className="px-4 py-4"></td>}
                                <td className="px-4 py-4 text-right font-bold">{formatNumber(summary.before)}</td>
                                {canSeeCost && <td className="px-4 py-4 text-right">{formatNumber(summary.cost)}</td>}
                                {canSeeProfit && <td className={`px-4 py-4 text-right ${summary.profit > 0 ? "text-green-600" : summary.profit < 0 ? "text-red-500" : ""}`}>{formatNumber(summary.profit)}</td>}
                             </tr>
                         </tfoot>
                     </table>
                 </div>
            </div>
        )}
      </div>
    </div>
  );
}

function RowGroup({ isOpen, onToggle, row, canSeeCost, canSeeProfit, formatNumber }) {
    return (
        <>
            <tr className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-none">
                 <td className="px-4 py-4">
                    <button 
                        onClick={onToggle}
                        className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
                    >
                        {isOpen ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowRightIcon fontSize="small"/>}
                    </button>
                </td>
                <td className="px-4 py-4 text-gray-600">{row.productCode ?? "-"}</td>
                <td className="px-4 py-4 font-medium text-gray-800">{row.productTitle ?? "-"}</td>
                <td className="px-4 py-4 text-gray-600">{row.categoryMain ?? "-"}</td>
                <td className="px-4 py-4 text-gray-600">{row.categorySub ?? "-"}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNumber(row.sendWeight)}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNumber(row.deductWeight)}</td>
                <td className="px-4 py-4 text-right font-medium text-gray-800">{formatNumber(row.destWeight)}</td>
                <td className="px-4 py-4 text-gray-500">{row.unit || "กก."}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNumber(row.avgPrice)}</td>
                {canSeeCost && (
                     <td className="px-4 py-4 text-right text-gray-500">{formatNumber(row.avgCost)}</td>
                )}
                <td className="px-4 py-4 text-right font-bold text-gray-800">{formatNumber(row.amountBeforeVat)}</td>
                {canSeeCost && (
                     <td className="px-4 py-4 text-right text-gray-500">{formatNumber(row.costAmount)}</td>
                )}
                {canSeeProfit && (
                     <td className={`px-4 py-4 text-right font-medium ${
                        (row.profitBeforeTax || 0) > 0 ? 'text-green-600' : (row.profitBeforeTax || 0) < 0 ? 'text-red-500' : 'text-gray-600'
                     }`}>
                        {formatNumber(row.profitBeforeTax)}
                    </td>
                )}
            </tr>

            {isOpen && (
                <tr className="bg-gray-50/50">
                    <td colSpan={canSeeCost && canSeeProfit ? 14 : canSeeCost || canSeeProfit ? 13 : 12} className="px-4 py-4">
                         <div className="pl-12 pr-4">
                             <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                                <span className="w-1 h-4 bg-green-500 rounded-full"></span>
                                รายการลูกค้าในสินค้าเดียวกัน
                            </h4>
                             <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                 <table className="w-full text-sm text-left">
                                     <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
                                         <tr>
                                            <th className="px-4 py-2 font-medium">ลูกค้า</th>
                                            <th className="px-4 py-2 text-right font-medium">น้ำหนักส่ง</th>
                                            <th className="px-4 py-2 text-right font-medium">หัก</th>
                                            <th className="px-4 py-2 text-right font-medium">สุทธิ</th>
                                            <th className="px-4 py-2 font-medium">หน่วย</th>
                                            <th className="px-4 py-2 text-right font-medium">ราคาขาย</th>
                                            {canSeeCost && <th className="px-4 py-2 text-right font-medium">ราคาต้นทุน</th>}
                                            <th className="px-4 py-2 text-right font-medium">ยอดก่อนภาษี</th>
                                            {canSeeCost && <th className="px-4 py-2 text-right font-medium">ต้นทุน</th>}
                                            {canSeeProfit && <th className="px-4 py-2 text-right font-medium">กำไรก่อนภาษี</th>}
                                         </tr>
                                     </thead>
                                     <tbody className="divide-y divide-gray-100">
                                         {(row.children || []).map((c, i) => (
                                             <tr key={i} className="hover:bg-gray-50">
                                                 <td className="px-4 py-2 text-gray-800">{c.customerName ?? "-"}</td>
                                                 <td className="px-4 py-2 text-right text-gray-600">{formatNumber(c.sendWeight)}</td>
                                                 <td className="px-4 py-2 text-right text-gray-600">{formatNumber(c.deductWeight)}</td>
                                                 <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNumber(c.destWeight)}</td>
                                                 <td className="px-4 py-2 text-gray-500">{c.unit || "กก."}</td>
                                                 <td className="px-4 py-2 text-right text-gray-600">{formatNumber(c.pricePerUnit)}</td>
                                                 {canSeeCost && <td className="px-4 py-2 text-right text-gray-600">{formatNumber(c.costPerUnit)}</td>}
                                                 <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNumber(c.amountBeforeVat)}</td>
                                                 {canSeeCost && <td className="px-4 py-2 text-right text-gray-600">{formatNumber(c.costAmount)}</td>}
                                                 {canSeeProfit && (
                                                     <td className={`px-4 py-2 text-right font-medium ${c.profitBeforeTax > 0 ? 'text-green-600' : c.profitBeforeTax < 0 ? 'text-red-500' : 'text-gray-600'}`}>
                                                         {formatNumber(c.profitBeforeTax)}
                                                     </td>
                                                 )}
                                             </tr>
                                         ))}
                                     </tbody>
                                 </table>
                             </div>
                         </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// Helpers
function groupByProduct(data = [], invById = {}, invByName = {}) {
  const prodMap = {};

  (Array.isArray(data) ? data : [data]).forEach((row) => {
    // const isVat = row?.isVat === true;

    (row.items || []).forEach((it) => {
      const pid = it.productId ?? it.product?.id ?? null;
      const pcode = it.product?.code ?? it.code ?? pid ?? "";
      const ptitle = String(it.productName ?? it.product?.title ?? "-").trim();

      const unit = it.unit || "กก.";
      const destW = num(it.netWeight ?? it.weight);
      const sendW = num(it.sendWeight ?? destW);

      const pricePerUnit = num(it.pricePerUnit);
      const amountBefore = num(it.amountBeforeVat ?? destW * pricePerUnit);

      // ---- cost from inventory ----
      const invCostById =
        pid != null ? invById[String(pid)]?.avgCostPerUnit : undefined;
      const invCostByName = ptitle
        ? invByName[ptitle]?.avgCostPerUnit
        : undefined;
      const costPerUnit = num(
        it.costPerUnit ?? invCostById ?? invCostByName ?? 0
      );
      const costAmount = num(it.costAmount ?? costPerUnit * destW);

      const key = String(pid ?? ptitle);
      if (!prodMap[key]) {
        prodMap[key] = {
          productId: pid,
          productCode: pcode,
          productTitle: ptitle,
          categoryMain: it.product?.category?.name ?? it.category ?? "-",
          categorySub: it.product?.subCategory?.name ?? it.subCategory ?? "-",
          unit,
          sendWeight: 0,
          destWeight: 0,
          deductWeight: 0,
          amountBeforeVat: 0,
          costAmount: 0,
          profitBeforeTax: 0,
          _childMap: new Map(),
        };
      }

      const rec = prodMap[key];
      rec.sendWeight += sendW;
      rec.destWeight += destW;
      rec.deductWeight += sendW - destW;
      rec.amountBeforeVat += amountBefore;
      rec.costAmount += costAmount;
      rec.profitBeforeTax += amountBefore - costAmount;

      // ===== รวมลูกค้าที่ซ้ำกันใน sub =====
      const customerId = row.customerId ?? row.customer?.id ?? null;
      const customerName = row.customer?.name ?? row.customerName ?? "-";
      const custKey = String(customerId ?? customerName);

      if (!rec._childMap.has(custKey)) {
        rec._childMap.set(custKey, {
          customerId,
          customerName,
          unit,
          sendWeight: 0,
          destWeight: 0,
          deductWeight: 0,
          _amountBeforeVat: 0,
          _costAmount: 0,
        });
      }

      const child = rec._childMap.get(custKey);
      child.sendWeight += sendW;
      child.destWeight += destW;
      child.deductWeight += sendW - destW;
      child._amountBeforeVat += amountBefore;
      child._costAmount += costAmount;
    });
  });

  // แปลง children และคำนวณราคาเฉลี่ยถ่วงน้ำหนัก
  return Object.values(prodMap).map((r) => {
    const children = Array.from(r._childMap.values()).map((c) => {
      const d = c.destWeight || 0;
      const pricePerUnit = d > 0 ? c._amountBeforeVat / d : 0;
      const costPerUnit = d > 0 ? c._costAmount / d : 0;
      const amountBeforeVat = c._amountBeforeVat;
      const costAmount = c._costAmount;
      const profitBeforeTax = amountBeforeVat - costAmount;

      return {
        customerId: c.customerId,
        customerName: c.customerName,
        unit: c.unit,
        sendWeight: c.sendWeight,
        destWeight: c.destWeight,
        deductWeight: c.deductWeight,
        pricePerUnit,
        costPerUnit,
        amountBeforeVat,
        costAmount,
        profitBeforeTax,
      };
    });

    const denom = r.destWeight || 0;
    return {
      ...r,
      avgPrice: denom > 0 ? r.amountBeforeVat / denom : 0,
      avgCost: denom > 0 ? r.costAmount / denom : 0,
      children, // ใช้ children ที่รวมแล้ว
      _childMap: undefined,
    };
  });
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fix2(v) {
  return Number(num(v)).toFixed(2);
}
